import { AxiosInstance } from 'axios';
import {
  ListTagsParams,
  RelatedTag,
  Tag
} from '../types/tags';
import { buildOffsetParams } from '../util/pagination';
import {
  GetTagRelationshipsRequestSchema,
  GetRelatedTagsRequestSchema,
  ListTagsParamsSchema,
  RelatedTagSchema,
  TagSchema
} from '../z/tags';

export class TagsClient {
  constructor(
    private readonly http: AxiosInstance
    
  ) {}

  /**
   * List all tags with optional filtering and pagination.
   * 
   * This endpoint returns a paginated list of tags with optional filtering
   * by category and search terms. Useful for building tag selection interfaces
   * or browsing available tags by category.
   * 
   * @example
   * ```typescript
   * // Get all politics tags
   * const tags = await client.tags.list({ 
   *   category: 'politics', 
   *   limit: 20 
   * });
   * 
   * // Search for specific tags
   * const electionTags = await client.tags.list({ 
   *   search: 'election',
   *   limit: 10 
   * });
   * ```
   * 
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of tags to return (default: 10)
   * @param params.offset - Number of tags to skip (default: 0)
   * @param params.category - Filter by category (e.g., 'politics', 'sports')
   * @param params.search - Search term to filter tags by name
   * 
   * @returns Promise<Tag[]> - Array of tags
   * @throws {Error} When the API request fails or returns invalid data
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/list-tags} - API Documentation
   */
  async list(params?: ListTagsParams): Promise<Tag[]> {
    const validatedParams = params ? ListTagsParamsSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get('/tags', { params: queryParams });
      const validatedData = TagSchema.array().parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list tags: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a tag by its unique identifier.
   * 
   * This endpoint returns detailed information about a specific tag,
   * including its metadata, description, and category information.
   * 
   * @example
   * ```typescript
   * const tag = await client.tags.getById('123');
   * console.log('Tag Name:', tag.name);
   * console.log('Category:', tag.category);
   * console.log('Description:', tag.description);
   * ```
   * 
   * @param id - Tag ID
   * @returns Promise<Tag> - Tag data with metadata
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-tag-by-id} - API Documentation
   */
  async getById(id: string): Promise<Tag> {
            try {
      const response = await this.http.get(`/tags/${id}`);
      const validatedData = TagSchema.parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tag by ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a tag by its human-readable slug.
   * 
   * This endpoint returns detailed information about a tag using its
   * slug instead of the numeric ID. Slugs are more user-friendly
   * and stable identifiers.
   * 
   * @example
   * ```typescript
   * const tag = await client.tags.getBySlug('us-elections');
   * console.log('Tag:', tag.name);
   * console.log('Slug:', tag.slug);
   * ```
   * 
   * @param slug - Tag slug (human-readable identifier)
   * @returns Promise<Tag> - Tag data with metadata
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-tag-by-slug} - API Documentation
   */
  async getBySlug(slug: string): Promise<Tag> {
            try {
      const response = await this.http.get(`/tags/slug/${slug}`);
      const validatedData = TagSchema.parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tag by slug ${slug}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get related tags relationships by tag ID.
   * 
   * This endpoint returns relationship data between tags, showing how
   * tags are connected to each other with relationship types and strengths.
   * Useful for understanding tag hierarchies and connections.
   * 
   * @example
   * ```typescript
   * const relationships = await client.tags.getRelationshipsById('123');
   * console.log('Found', relationships.length, 'relationships');
   * 
   * relationships.forEach(rel => {
   *   console.log(`${rel.type}: ${rel.relatedTag.name} (strength: ${rel.strength})`);
   * });
   * ```
   * 
   * @param id - Tag ID
   * @param params - Optional query parameters
   * @param params.omit_empty - Whether to omit empty relationships
   * @param params.status - Filter by relationship status ('active', 'closed', 'all')
   * 
   * @returns Promise<RelatedTag[]> - Array of tag relationships
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-related-tags-relationships-by-tag-id} - API Documentation
   */
  async getRelationshipsById(id: string, params?: { omit_empty?: boolean; status?: 'active' | 'closed' | 'all' }): Promise<RelatedTag[]> {
    const validatedParams = params ? GetTagRelationshipsRequestSchema.parse({ ...params, id: Number(id) }) : { id: Number(id) };
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get(`/tags/${id}/relationships`, { 
        params: queryParams 
      });
      const validatedData = RelatedTagSchema.array().parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tag relationships for ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get related tags relationships by tag slug.
   * 
   * This endpoint returns relationship data between tags using the tag's
   * slug instead of ID. Shows how tags are connected with relationship
   * types and strengths.
   * 
   * @example
   * ```typescript
   * const relationships = await client.tags.getRelationshipsBySlug('us-elections');
   * console.log('Found', relationships.length, 'relationships for US Elections');
   * ```
   * 
   * @param slug - Tag slug (human-readable identifier)
   * @param params - Optional query parameters
   * @param params.omit_empty - Whether to omit empty relationships
   * @param params.status - Filter by relationship status ('active', 'closed', 'all')
   * 
   * @returns Promise<RelatedTag[]> - Array of tag relationships
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-related-tags-relationships-by-tag-slug} - API Documentation
   */
  async getRelationshipsBySlug(slug: string, params?: { omit_empty?: boolean; status?: 'active' | 'closed' | 'all' }): Promise<RelatedTag[]> {
    const queryParams = params ? buildOffsetParams(params) : {};
            try {
      const response = await this.http.get(`/tags/slug/${slug}/relationships`, { 
        params: queryParams 
      });
      const validatedData = RelatedTagSchema.array().parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tag relationships for slug ${slug}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get tags related to a specific tag by ID.
   * 
   * This endpoint returns a list of tags that are related to the specified
   * tag. Useful for discovering similar or related content categories.
   * 
   * @example
   * ```typescript
   * const relatedTags = await client.tags.getRelatedToId('123');
   * console.log('Related tags:', relatedTags.map(tag => tag.name));
   * 
   * // With pagination
   * const moreRelated = await client.tags.getRelatedToId('123', { 
   *   limit: 5, 
   *   offset: 10 
   * });
   * ```
   * 
   * @param id - Tag ID
   * @param params - Optional query parameters for pagination
   * @param params.limit - Number of related tags to return (default: 10)
   * @param params.offset - Number of related tags to skip (default: 0)
   * 
   * @returns Promise<Tag[]> - Array of related tags
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-tags-related-to-a-tag-id} - API Documentation
   */
  async getRelatedToId(id: string, params?: { limit?: number; offset?: number }): Promise<Tag[]> {
    const validatedParams = params ? GetRelatedTagsRequestSchema.parse({ ...params, id }) : { id };
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get(`/tags/${id}/related`, { 
        params: queryParams 
      });
      const validatedData = TagSchema.array().parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get related tags for ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get tags related to a specific tag by slug.
   * 
   * This endpoint returns a list of tags that are related to the specified
   * tag using its slug. Useful for discovering similar or related content
   * categories with human-readable identifiers.
   * 
   * @example
   * ```typescript
   * const relatedTags = await client.tags.getRelatedToSlug('us-elections');
   * console.log('Tags related to US Elections:', relatedTags.map(tag => tag.name));
   * 
   * // With pagination
   * const moreRelated = await client.tags.getRelatedToSlug('us-elections', { 
   *   limit: 5, 
   *   offset: 10 
   * });
   * ```
   * 
   * @param slug - Tag slug (human-readable identifier)
   * @param params - Optional query parameters for pagination
   * @param params.limit - Number of related tags to return (default: 10)
   * @param params.offset - Number of related tags to skip (default: 0)
   * 
   * @returns Promise<Tag[]> - Array of related tags
   * @throws {Error} When the tag is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/tags/get-tags-related-to-a-tag-slug} - API Documentation
   */
  async getRelatedToSlug(slug: string, params?: { limit?: number; offset?: number }): Promise<Tag[]> {
    const queryParams = params ? buildOffsetParams(params) : {};
            try {
      const response = await this.http.get(`/tags/slug/${slug}/related`, { 
        params: queryParams 
      });
      const validatedData = TagSchema.array().parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get related tags for slug ${slug}: ${error.message}`);
      }
      throw error;
    }
  }
}
